import type { ReactNode } from 'react';
import { Header, Message, Segment } from 'semantic-ui-react';
import type { DescriptorType } from '../../../../../../../../types/descriptor.types';
import { CalculationExpUtil } from '../../../../../../../../utils/descriptor/calculationExp';
import DescriptorUtils from '../../../../../../../../utils/descriptor/descriptorUtils';
import { MODEL_DIFF_DISPLAY_MODE, REPORTS_DIFF_AVAILABILITY } from '../../constants';
import { buildDescriptorDiffs } from '../../descriptorDiff';
import ReportsChangesPreview from './components/reportsChangesPreview';
import DetailedReports from './components/detailedReports';
import { ModelContent } from './tableParts';
import type { ComparisonTableProps, GetTooltipContent } from './types';
import Assert from '../../../../../../../../utils/asserts';

const originalToHumanReadable = CalculationExpUtil.originalToHumanReadable as (
    calculation: string,
    flatDescriptors: DescriptorType[],
) => string;

function BlurredReports({
    modelContent,
    message,
    unavailable = false,
}: {
    modelContent?: ReactNode;
    message: string;
    unavailable?: boolean;
}) {
    const reportPlaceholder = (
        <div className="branch-comparison-details__locked-reports">
            <div className="branch-comparison-details__locked-placeholders" aria-hidden="true">
                <span />
                <span />
                <span />
            </div>
            <Message warning={!unavailable} error={unavailable}>
                {message}
            </Message>
        </div>
    );

    return modelContent ? (
        <div className="branch-comparison-details__locked-layout">
            {modelContent}
            {reportPlaceholder}
        </div>
    ) : (
        reportPlaceholder
    );
}

function ComparisonTable({ comparison, summarized, coreDataDiffOnly, companyId }: ComparisonTableProps) {
    const { tableDiff, referenceBranch, targetBranch, modelDisplayMode, reportsAvailability } = comparison;
    const columnLabels = { reference: referenceBranch.type, target: targetBranch.type };
    const flatTargetDescriptors = (
        DescriptorUtils.flatDescriptors as (descriptors: DescriptorType[]) => DescriptorType[]
    )(targetBranch.schema.descriptors);
    const flatReferenceDescriptors = (
        DescriptorUtils.flatDescriptors as (descriptors: DescriptorType[]) => DescriptorType[]
    )(referenceBranch.schema.descriptors);
    const flatDescriptors = [
        ...new Set([...flatTargetDescriptors, ...flatReferenceDescriptors].map((descriptor) => descriptor.id)),
    ]
        .map((id) => {
            const descriptor =
                flatTargetDescriptors.find((descriptor) => descriptor.id === id) ??
                flatReferenceDescriptors.find((descriptor) => descriptor.id === id);

            Assert.notNullOrUndefined(
                descriptor,
                `Descriptor with id ${id} not found in either target or reference descriptors`,
            );
            return descriptor;
        })
        .filter((descriptor): descriptor is DescriptorType => descriptor !== undefined);
    const modelDiffs = buildDescriptorDiffs(
        tableDiff.schemaDiff,
        tableDiff.reportsDiff.diffDescriptors,
        flatTargetDescriptors,
    );

    const getTooltipContent: GetTooltipContent = (descriptorId, label) => {
        const descriptor = flatDescriptors.find((field) => field.id === descriptorId)!;
        return (
            <>
                <div>{label}</div>
                {DescriptorUtils.hasCalculation(descriptor) && (
                    <div>
                        <b>Calculation:</b> {originalToHumanReadable(descriptor.calculation, flatDescriptors)}
                    </div>
                )}
                {DescriptorUtils.hasSecondaryCalculation(descriptor) && (
                    <div>
                        <b>Secondary Calculation:</b>{' '}
                        {originalToHumanReadable(descriptor.secondaryCalculation, flatDescriptors)}
                    </div>
                )}
            </>
        );
    };

    function renderContent() {
        if (modelDisplayMode === MODEL_DIFF_DISPLAY_MODE.COUNT_ONLY) {
            return (
                <BlurredReports
                    modelContent={<div>Model Differences: {modelDiffs.length}</div>}
                    message="Detailed Reports Differences are not available at this stage"
                    unavailable
                />
            );
        }

        switch (reportsAvailability) {
            case REPORTS_DIFF_AVAILABILITY.AVAILABLE:
                return (
                    <DetailedReports
                        comparison={comparison}
                        summarized={summarized}
                        coreDataDiffOnly={coreDataDiffOnly}
                        companyId={companyId}
                        modelDiffs={modelDiffs}
                        columnLabels={columnLabels}
                        getTooltipContent={getTooltipContent}
                    />
                );
            case REPORTS_DIFF_AVAILABILITY.REPORTS_CHANGES_PREVIEW:
                return (
                    <ReportsChangesPreview
                        comparison={comparison}
                        companyId={companyId}
                        columnLabels={columnLabels}
                        getTooltipContent={getTooltipContent}
                    />
                );
            case REPORTS_DIFF_AVAILABILITY.BLOCKED_BY_MODEL_DIFFS:
                return (
                    <BlurredReports
                        modelContent={
                            <ModelContent
                                diffs={modelDiffs}
                                columnLabels={columnLabels}
                                getTooltipContent={getTooltipContent}
                            />
                        }
                        message="Please resolve model differences before comparing reports."
                    />
                );
            case REPORTS_DIFF_AVAILABILITY.UNAVAILABLE:
                return (
                    <BlurredReports
                        modelContent={
                            modelDiffs.length > 0 ? (
                                <ModelContent
                                    diffs={modelDiffs}
                                    columnLabels={columnLabels}
                                    getTooltipContent={getTooltipContent}
                                />
                            ) : undefined
                        }
                        message="Detailed Reports Differences are not available at this stage"
                        unavailable
                    />
                );
        }
    }

    return (
        <Segment className="branch-comparison-details__section">
            <Header as="h5" dividing>
                Differences
            </Header>
            {renderContent()}
        </Segment>
    );
}

export default ComparisonTable;
